import { createId } from '../lib/ids';
import { mulberry32 } from '../music/random';
import { DEFAULT_PITCH, createSceneElement, createSection, defaultNoteLength, demoPattern, emptyPattern, activeScene } from './factories';
import {
  BPM_MAX,
  BPM_MIN,
  DRUM_INSTRUMENTS,
  MAX_NOTES_PER_TRACK,
  MAX_PATTERNS,
  MAX_SECTION_BARS,
  MAX_SECTIONS,
  MAX_SONG_BARS,
  MELODIC_INSTRUMENTS,
  type AudioBus,
  type Instrument,
  type MusicalKey,
  type Motion,
  type NoteEvent,
  type Pattern,
  type Project,
  type Scene,
  type Section,
  type SectionKind,
  type Shape,
  type Track,
  type TrackEffects,
} from './schemas';

/**
 * Pure, immutable project transformations shared by the UI and the prompt executor.
 * Every function returns a new Project and leaves unrelated references untouched so
 * React and the audio engine can cheaply detect what changed. Operations that would
 * break a limit throw `OpError` with a user-facing message and change nothing.
 */
export class OpError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OpError';
  }
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const round2 = (v: number) => Math.round(v * 100) / 100;

// ---------------------------------------------------------------- Settings

export function setBpm(project: Project, bpm: number): Project {
  const value = Math.round(clamp(bpm, BPM_MIN, BPM_MAX));
  if (value === project.settings.bpm) return project;
  return { ...project, settings: { ...project.settings, bpm: value } };
}

export function setSwing(project: Project, swing: number): Project {
  return { ...project, settings: { ...project.settings, swing: round2(clamp(swing, 0, 0.5)) } };
}

export function setMasterVolume(project: Project, db: number): Project {
  return { ...project, settings: { ...project.settings, masterVolumeDb: round2(clamp(db, -60, 0)) } };
}

export function setKey(project: Project, key: MusicalKey): Project {
  return { ...project, settings: { ...project.settings, key } };
}

export function renameProject(project: Project, name: string): Project {
  return { ...project, name: name.slice(0, 80) };
}

export function setDescription(project: Project, description: string): Project {
  return { ...project, description: description.slice(0, 2000) };
}

export function setTransportMode(project: Project, mode: Project['transport']['mode']): Project {
  if (project.transport.mode === mode) return project;
  return { ...project, transport: { ...project.transport, mode } };
}

export function setLoopSong(project: Project, loopSong: boolean): Project {
  return { ...project, transport: { ...project.transport, loopSong } };
}

// ---------------------------------------------------------------- Tracks & mixer

export function updateTrack(project: Project, instrument: Instrument, patch: Partial<Omit<Track, 'id' | 'instrument'>>): Project {
  if (!project.tracks.some((t) => t.instrument === instrument)) throw new OpError(`This project has no ${instrument} track.`);
  return { ...project, tracks: project.tracks.map((t) => (t.instrument === instrument ? { ...t, ...patch } : t)) };
}

const trackOf = (project: Project, instrument: Instrument): Track => {
  const track = project.tracks.find((t) => t.instrument === instrument);
  if (!track) throw new OpError(`This project has no ${instrument} track.`);
  return track;
};

export const setMuted = (p: Project, i: Instrument, muted: boolean) => updateTrack(p, i, { muted });
export const setSolo = (p: Project, i: Instrument, solo: boolean) => updateTrack(p, i, { solo });
export const setTrackVolume = (p: Project, i: Instrument, volumeDb: number) =>
  updateTrack(p, i, { volumeDb: round2(clamp(volumeDb, -60, 6)) });
export const adjustTrackVolume = (p: Project, i: Instrument, deltaDb: number) =>
  setTrackVolume(p, i, trackOf(p, i).volumeDb + deltaDb);
export const setPan = (p: Project, i: Instrument, pan: number) => updateTrack(p, i, { pan: round2(clamp(pan, -1, 1)) });

const EFFECT_LIMITS: Record<keyof TrackEffects, [number, number]> = {
  drive: [0, 1],
  cutoffHz: [80, 20000],
  delaySend: [0, 1],
  reverbSend: [0, 1],
};

export function setEffect(project: Project, instrument: Instrument, key: keyof TrackEffects, value: number): Project {
  const [min, max] = EFFECT_LIMITS[key];
  const v = key === 'cutoffHz' ? Math.round(clamp(value, min, max)) : round2(clamp(value, min, max));
  const track = trackOf(project, instrument);
  return updateTrack(project, instrument, { effects: { ...track.effects, [key]: v } });
}

/**
 * Tracks that are audible given mute and solo states (solo wins over everything else).
 * The audio clip bus takes part in solo like any other channel.
 */
export function audibleInstruments(tracks: readonly Track[], bus?: Pick<AudioBus, 'solo'>): Set<Instrument> {
  const soloed = tracks.some((t) => t.solo) || !!bus?.solo;
  return new Set(tracks.filter((t) => !t.muted && (!soloed || t.solo)).map((t) => t.instrument));
}

export function busAudible(tracks: readonly Track[], bus: Pick<AudioBus, 'solo' | 'muted'>): boolean {
  const soloed = tracks.some((t) => t.solo) || bus.solo;
  return !bus.muted && (!soloed || bus.solo);
}

// ---------------------------------------------------------------- Patterns & notes

function patternOf(project: Project, patternId: string): Pattern {
  const pattern = project.patterns.find((p) => p.id === patternId);
  if (!pattern) throw new OpError('That pattern no longer exists.');
  return pattern;
}

export function mapPattern(project: Project, patternId: string, fn: (pattern: Pattern) => Pattern): Project {
  patternOf(project, patternId);
  return { ...project, patterns: project.patterns.map((p) => (p.id === patternId ? fn(p) : p)) };
}

function setNotes(pattern: Pattern, instrument: Instrument, notes: NoteEvent[]): Pattern {
  if (notes.length > MAX_NOTES_PER_TRACK) throw new OpError(`A track can hold at most ${MAX_NOTES_PER_TRACK} notes per pattern.`);
  const sorted = [...notes].sort((a, b) => a.step - b.step || a.pitch - b.pitch);
  return { ...pattern, tracks: { ...pattern.tracks, [instrument]: sorted } };
}

const notesIn = (pattern: Pattern, instrument: Instrument): NoteEvent[] => [...(pattern.tracks[instrument] ?? [])];

/** Drum grid toggle: removes every hit on the step, or adds one with default tuning. */
export function toggleDrumStep(project: Project, patternId: string, instrument: Instrument, step: number): Project {
  return mapPattern(project, patternId, (pattern) => {
    if (step < 0 || step >= pattern.lengthSteps) throw new OpError('Step is outside the pattern.');
    const notes = notesIn(pattern, instrument);
    const hit = notes.some((n) => n.step === step);
    return setNotes(
      pattern,
      instrument,
      hit
        ? notes.filter((n) => n.step !== step)
        : [...notes, { step, pitch: DEFAULT_PITCH[instrument], velocity: instrument === 'hats' ? 0.6 : 0.9, length: 1, offset: 0 }],
    );
  });
}

/** Melodic grid toggle at (step, pitch). Returns the project and whether a note now exists there. */
export function toggleNote(project: Project, patternId: string, instrument: Instrument, step: number, pitch: number): Project {
  return mapPattern(project, patternId, (pattern) => {
    if (step < 0 || step >= pattern.lengthSteps) throw new OpError('Step is outside the pattern.');
    const notes = notesIn(pattern, instrument);
    const index = notes.findIndex((n) => n.step === step && n.pitch === pitch);
    if (index >= 0) notes.splice(index, 1);
    else notes.push({ step, pitch, velocity: 0.85, length: defaultNoteLength(instrument), offset: 0 });
    return setNotes(pattern, instrument, notes);
  });
}

/** Edits a note identified by (step, pitch). Values are clamped to valid ranges. */
export function updateNote(
  project: Project,
  patternId: string,
  instrument: Instrument,
  at: { step: number; pitch: number },
  patch: Partial<Pick<NoteEvent, 'length' | 'velocity' | 'offset' | 'pitch'>>,
): Project {
  return mapPattern(project, patternId, (pattern) => {
    const notes = notesIn(pattern, instrument);
    const index = notes.findIndex((n) => n.step === at.step && n.pitch === at.pitch);
    if (index < 0) throw new OpError('That note no longer exists.');
    const current = notes[index]!;
    if (patch.pitch !== undefined && patch.pitch !== current.pitch && notes.some((n) => n.step === current.step && n.pitch === Math.round(patch.pitch!))) {
      throw new OpError('There is already a note at that pitch on this step.');
    }
    notes[index] = {
      ...current,
      length: patch.length !== undefined ? round2(clamp(patch.length, 0.25, 64)) : current.length,
      velocity: patch.velocity !== undefined ? round2(clamp(patch.velocity, 0.05, 1)) : current.velocity,
      offset: patch.offset !== undefined ? round2(clamp(patch.offset, -0.5, 0.5)) : current.offset,
      pitch: patch.pitch !== undefined ? Math.round(clamp(patch.pitch, 0, 127)) : current.pitch,
    };
    return setNotes(pattern, instrument, notes);
  });
}

export function clearInstrument(project: Project, patternId: string, instrument: Instrument): Project {
  return mapPattern(project, patternId, (pattern) => setNotes(pattern, instrument, []));
}

export function setPatternNotes(project: Project, patternId: string, tracks: Pattern['tracks']): Project {
  return mapPattern(project, patternId, (pattern) => ({ ...pattern, tracks: { ...pattern.tracks, ...tracks } }));
}

/** Restores the demo groove into the given pattern (Phase 0 "reset pattern"). */
export function resetPatternToDemo(project: Project, patternId: string): Project {
  return mapPattern(project, patternId, (pattern) => ({ ...pattern, lengthSteps: 16, tracks: demoPattern().tracks }));
}

export function setActivePattern(project: Project, patternId: string): Project {
  patternOf(project, patternId);
  return project.activePatternId === patternId ? project : { ...project, activePatternId: patternId };
}

export function addPattern(project: Project, name?: string): Project {
  if (project.patterns.length >= MAX_PATTERNS) throw new OpError(`A project can hold at most ${MAX_PATTERNS} patterns.`);
  const pattern = emptyPattern(name ?? `Pattern ${project.patterns.length + 1}`);
  return { ...project, patterns: [...project.patterns, pattern], activePatternId: pattern.id };
}

export function duplicatePattern(project: Project, patternId: string): Project {
  if (project.patterns.length >= MAX_PATTERNS) throw new OpError(`A project can hold at most ${MAX_PATTERNS} patterns.`);
  const source = patternOf(project, patternId);
  const copy: Pattern = { ...structuredClone(source), id: createId('pat'), name: `${source.name} copy`.slice(0, 80) };
  const index = project.patterns.indexOf(source);
  const patterns = [...project.patterns];
  patterns.splice(index + 1, 0, copy);
  return { ...project, patterns, activePatternId: copy.id };
}

export function renamePattern(project: Project, patternId: string, name: string): Project {
  return mapPattern(project, patternId, (p) => ({ ...p, name: name.slice(0, 80) }));
}

/** Changes pattern length; notes beyond a shorter length are removed. */
export function setPatternLength(project: Project, patternId: string, lengthSteps: Pattern['lengthSteps']): Project {
  return mapPattern(project, patternId, (pattern) => {
    const tracks: Pattern['tracks'] = {};
    for (const [instrument, notes] of Object.entries(pattern.tracks) as [Instrument, NoteEvent[]][]) {
      tracks[instrument] = notes.filter((n) => n.step < lengthSteps);
    }
    return { ...pattern, lengthSteps, tracks };
  });
}

export function removePattern(project: Project, patternId: string): Project {
  patternOf(project, patternId);
  if (project.patterns.length <= 1) throw new OpError('A project needs at least one pattern.');
  if (project.arrangement.sections.some((s) => s.patternId === patternId)) {
    throw new OpError('This pattern is used in the arrangement. Change those sections first.');
  }
  const patterns = project.patterns.filter((p) => p.id !== patternId);
  return {
    ...project,
    patterns,
    activePatternId: project.activePatternId === patternId ? patterns[0]!.id : project.activePatternId,
  };
}

// ---------------------------------------------------------------- Arrangement

export const songBars = (project: Project): number => project.arrangement.sections.reduce((sum, s) => sum + s.bars, 0);

function withSections(project: Project, sections: Section[]): Project {
  if (sections.length > MAX_SECTIONS) throw new OpError(`An arrangement can hold at most ${MAX_SECTIONS} sections.`);
  const bars = sections.reduce((sum, s) => sum + s.bars, 0);
  if (bars > MAX_SONG_BARS) throw new OpError(`The song would be ${bars} bars long; the limit is ${MAX_SONG_BARS}.`);
  return { ...project, arrangement: { sections } };
}

function sectionIndex(project: Project, sectionId: string): number {
  const index = project.arrangement.sections.findIndex((s) => s.id === sectionId);
  if (index < 0) throw new OpError('That section no longer exists.');
  return index;
}

export function addSection(project: Project, kind: SectionKind, patternId: string, bars: number, index?: number): Project {
  patternOf(project, patternId);
  if (!Number.isInteger(bars) || bars < 1 || bars > MAX_SECTION_BARS) {
    throw new OpError(`A section must be 1–${MAX_SECTION_BARS} bars long.`);
  }
  const sections = [...project.arrangement.sections];
  sections.splice(index ?? sections.length, 0, createSection(kind, patternId, bars));
  return withSections(project, sections);
}

export function duplicateSection(project: Project, sectionId: string): Project {
  const index = sectionIndex(project, sectionId);
  const source = project.arrangement.sections[index]!;
  const sections = [...project.arrangement.sections];
  sections.splice(index + 1, 0, { ...source, id: createId('sec') });
  return withSections(project, sections);
}

export function removeSection(project: Project, sectionId: string): Project {
  const index = sectionIndex(project, sectionId);
  return withSections(project, project.arrangement.sections.filter((_, i) => i !== index));
}

export function moveSection(project: Project, sectionId: string, delta: number): Project {
  const index = sectionIndex(project, sectionId);
  const target = clamp(index + delta, 0, project.arrangement.sections.length - 1);
  if (target === index) return project;
  const sections = [...project.arrangement.sections];
  const [moved] = sections.splice(index, 1);
  sections.splice(target, 0, moved!);
  return withSections(project, sections);
}

export function resizeSection(project: Project, sectionId: string, bars: number): Project {
  const index = sectionIndex(project, sectionId);
  const value = Math.round(bars);
  if (value < 1 || value > MAX_SECTION_BARS) throw new OpError(`A section must be 1–${MAX_SECTION_BARS} bars long.`);
  return withSections(
    project,
    project.arrangement.sections.map((s, i) => (i === index ? { ...s, bars: value } : s)),
  );
}

export function updateSection(project: Project, sectionId: string, patch: Partial<Pick<Section, 'kind' | 'name' | 'patternId'>>): Project {
  const index = sectionIndex(project, sectionId);
  if (patch.patternId) patternOf(project, patch.patternId);
  return withSections(
    project,
    project.arrangement.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
  );
}

// ---------------------------------------------------------------- Musical transforms

const DRUM_GROUP: readonly Instrument[] = DRUM_INSTRUMENTS;
export type InstrumentGroup = 'drums' | 'bass' | 'melody' | 'all';

export function groupInstruments(group: InstrumentGroup): readonly Instrument[] {
  switch (group) {
    case 'drums':
      return DRUM_GROUP;
    case 'bass':
      return ['bass808'];
    case 'melody':
      return ['cowbell', 'lead', 'pad'];
    case 'all':
      return [...DRUM_GROUP, ...MELODIC_INSTRUMENTS];
  }
}

/**
 * Deterministically raises (or lowers) the energy of a group across every pattern:
 * velocities scale ±15 %, and for drums hats/percussion gain (or lose) off-beat ghost
 * notes; drive on the group's tracks moves ±0.1. Same input + seed → same output.
 */
export function adjustIntensity(project: Project, group: InstrumentGroup, direction: 1 | -1, seed: number): Project {
  const instruments = groupInstruments(group);
  const rand = mulberry32(seed);
  const patterns = project.patterns.map((pattern) => {
    const tracks: Pattern['tracks'] = { ...pattern.tracks };
    for (const instrument of instruments) {
      let notes = notesIn(pattern, instrument).map((n) => ({
        ...n,
        velocity: round2(clamp(n.velocity * (direction > 0 ? 1.15 : 0.85), 0.05, 1)),
      }));
      if (instrument === 'hats' || instrument === 'perc') {
        if (direction > 0) {
          const taken = new Set(notes.map((n) => n.step));
          for (let step = 1; step < pattern.lengthSteps; step += 2) {
            if (!taken.has(step) && rand() < 0.45 && notes.length < MAX_NOTES_PER_TRACK) {
              notes.push({ step, pitch: DEFAULT_PITCH[instrument], velocity: 0.35, length: 1, offset: 0 });
            }
          }
        } else {
          notes = notes.filter((n) => n.step % 2 === 0 || n.velocity >= 0.5 || rand() < 0.3);
        }
      }
      if (notes.length || tracks[instrument]) tracks[instrument] = notes.sort((a, b) => a.step - b.step || a.pitch - b.pitch);
    }
    return { ...pattern, tracks };
  });
  const tracks = project.tracks.map((t) =>
    instruments.includes(t.instrument) && t.instrument !== 'pad'
      ? { ...t, effects: { ...t.effects, drive: round2(clamp(t.effects.drive + 0.1 * direction, 0, 1)) } }
      : t,
  );
  return { ...project, patterns, tracks };
}

/**
 * Builds a buildup pattern from a source pattern: no kick or 808, melodic parts kept,
 * and an accelerating snare roll (8ths → 16ths) with rising velocity into the next section.
 */
export function buildupFrom(source: Pattern, name = 'Buildup'): Pattern {
  const length = source.lengthSteps;
  const snare: NoteEvent[] = [];
  for (let step = 0; step < length; step++) {
    const progress = step / length;
    const every = progress < 0.5 ? 4 : progress < 0.75 ? 2 : 1;
    if (step % every === 0) snare.push({ step, pitch: DEFAULT_PITCH.snare, velocity: round2(0.3 + 0.7 * progress), length: 1, offset: 0 });
  }
  const tracks: Pattern['tracks'] = {};
  for (const [instrument, notes] of Object.entries(source.tracks) as [Instrument, NoteEvent[]][]) {
    if (instrument === 'kick' || instrument === 'bass808' || instrument === 'snare' || instrument === 'clap') continue;
    tracks[instrument] = notes;
  }
  tracks.snare = snare;
  return { id: createId('pat'), name, lengthSteps: length, tracks };
}

// ---------------------------------------------------------------- Animation

function mapActiveScene(project: Project, fn: (s: Scene) => Scene): Project {
  return { ...project, scenes: project.scenes.map((s) => (s.id === project.activeSceneId ? fn(s) : s)) };
}

export function setBackground(project: Project, color: string): Project {
  return mapActiveScene(project, (s) => ({ ...s, background: color }));
}

export function setSceneSpeed(project: Project, speed: number): Project {
  return mapActiveScene(project, (s) => ({ ...s, speed: Math.round(clamp(speed, 0.1, 5) * 100) / 100 }));
}

export const MAX_ELEMENTS = 64;

export function addShape(project: Project, shape: Shape, color: string, motion: Motion): Project {
  const scene = activeScene(project);
  if (scene.elements.length >= MAX_ELEMENTS) throw new Error(`A scene can hold at most ${MAX_ELEMENTS} shapes.`);
  const n = scene.elements.length + 1;
  // Deterministic spread so new shapes do not stack on top of each other.
  const element = createSceneElement({
    shape,
    color,
    motion,
    x: Math.round((0.2 + ((n * 0.37) % 0.6)) * 1000) / 1000,
    y: Math.round((0.25 + ((n * 0.53) % 0.5)) * 1000) / 1000,
    size: 0.07,
    phase: Math.round(((n * 0.13) % 1) * 1000) / 1000,
  });
  return mapActiveScene(project, (s) => ({ ...s, elements: [...s.elements, element] }));
}

export function removeShape(project: Project, elementId: string): Project {
  return mapActiveScene(project, (s) => ({ ...s, elements: s.elements.filter((e) => e.id !== elementId) }));
}

export function clearShapes(project: Project): Project {
  return mapActiveScene(project, (s) => ({ ...s, elements: [] }));
}
